// View 7 — Framework Editor (inline-nested, single-page).
//
// Per design/UI : UX v1.0/for-engineering-frameworks/design-frameworks/
// specs/frameworks-strings.md § View 7 + README.md § 3 Q-14:
// "inline nested on one scrollable page (phases → activities), not modals
//  — fast for Olivier authoring dozens."
//
// Routes (registered in App.tsx):
//   /admin/knowledge/frameworks/new   — start a fresh draft; POST on first save
//   /admin/knowledge/frameworks/:id   — edit existing; PATCH on each save
//
// V2 scope:
//   - Meta (title · domain · short/long desc · estimated effort · tags)
//   - Maturity target (domain + level — the framework's outcome contract)
//   - Deliverables repeater + persistent Deliverables rail (with dynamic
//     "template claimed / no template yet" chip per rail entry)
//   - Phases — ordered list, tags, 0..N reference documents
//   - Activities — Duration bucket + tenant-override, cross-phase
//     Dependencies picker, Stakeholders combobox (starter list + freeform),
//     0..N Documents with role toggle (reference | deliverable_template)
//     and the forward-link produces_deliverable_ids picker
//   - Lifecycle footer: Save / Publish / Retire / Republish with inline confirms
//   - Publish-blocked validation surface (mirrors backend validateFrameworkPublishable)

import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@gc-coach/design-system';
import type {
  ActivityDocument,
  ActivityInput,
  CreateFrameworkRequest,
  DocumentRole,
  DocumentSource,
  Domain,
  DurationBucket,
  FrameworkDeliverable,
  FrameworkDetail,
  MaturityLevel,
  MaturityTarget,
  PatchFrameworkRequest,
  PhaseDocument,
  PhaseInput,
} from '@gc-coach/shared';
import {
  ALL_DOMAINS,
  ALL_DURATION_BUCKETS,
  ALL_MATURITY_LEVELS,
  STAKEHOLDER_STARTER_LIST,
} from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import {
  createFramework,
  getFramework,
  patchFramework,
  publishFramework,
  republishFramework,
  retireFramework,
} from '../api/endpoints.js';
import styles from './FrameworkEditorPage.module.css';

// Local editor state — mirrors FrameworkDetail but with client-side
// stable ids for unsaved phases/activities (prefix `tmp_…`). The backend's
// reconcilePhases handles minting canonical ids for new phases/activities
// and rewrites cross-phase activity dependency references through the
// activity id map.
// v2 · activity-level document. Same fields as PhaseDocument, plus a role
// toggle. When role === 'deliverable_template', produces_deliverable_ids
// carries the framework-level deliverable ids this template feeds into
// (multi-valued — one template can produce several deliverables per the
// Q4 revision, e.g. a Risk Map feeding both the Register and the Policy).
type LocalActivityDocument = {
  /** Local-only key for React + mutators. Never leaves the client. */
  localId: string;
  source: DocumentSource;
  role: DocumentRole;
  title: string;
  kb_entry_id: string;
  uploaded_file_ref: string;
  produces_deliverable_ids: string[];
};

type LocalActivity = {
  id: string;
  title: string;
  description: string;
  tags: string[];
  order: number;
  /** Bucket + tenant-override flag. Required at publish time. */
  duration: {
    default_months: DurationBucket | null;
    tenant_override_allowed: boolean;
  };
  /** Cross-phase-allowed activity → activity dependencies. */
  dependencies: string[];
  /** Multi-valued; suggests STAKEHOLDER_STARTER_LIST, freeform allowed. */
  stakeholders: string[];
  /** 0..N documents (reference or deliverable_template role). */
  documents: LocalActivityDocument[];
};

// v2 · phase-level document (reference material scoped to a whole phase).
// Role is locked to `reference` at phase level (deliverable templates live
// on activities). Client-side row uses a stable local id for React keys +
// remove targeting — it is NOT sent to the backend (PhaseDocument on the
// wire has no id field; docs are matched positionally within the phase).
type LocalPhaseDocument = {
  /** Local-only key for React + mutators. Never leaves the client. */
  localId: string;
  source: DocumentSource;
  title: string;
  /** Present iff source === 'kb_entry_ref'. */
  kb_entry_id: string;
  /** Present iff source === 'uploaded_file'. Storage ref; V1 = paste
   *  the id; proper drag-drop upload UX in V1.x. */
  uploaded_file_ref: string;
};

type LocalPhase = {
  id: string;
  title: string;
  description: string;
  tags: string[];
  order: number;
  activities: LocalActivity[];
  documents: LocalPhaseDocument[];
};

// v2 · framework-level output the tenant produces by running this framework
// (AI Governance Policy, Risk Register, Board Deck). Client mints a stable
// id at creation time so the backend can round-trip it unchanged (the
// backend does NOT rewrite deliverable ids — activity documents will
// reference these same ids via `produces_deliverable_ids` in MR 6).
type LocalDeliverable = {
  id: string;
  name: string;
  description: string;
  /** Optional — what "passes" looks like. Feeds the V1.x coach-evaluation loop. */
  evaluation_rubric: string;
};

type LocalState = {
  domain: Domain;
  title: string;
  short_description: string;
  long_description: string;
  tags: string[];
  estimated_effort_label: string | null;
  /**
   * v2 maturity contract — required for publish. Nullable while draft.
   * Both fields must be set to persist; if either is null we omit
   * maturity_target from the save payload.
   */
  maturity_target: {
    domain: Domain | null;
    level: MaturityLevel | null;
  };
  /**
   * v2 · framework-level deliverables (OUTPUTS). Required for publish
   * (≥1 declared, and — once MR 6 lands — every declared deliverable
   * claimed by at least one activity-level `deliverable_template` document).
   */
  deliverables: LocalDeliverable[];
  phases: LocalPhase[];
};

type EditorMode = 'new' | 'editing';
type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type PendingAction = 'publish' | 'retire' | 'republish' | null;

let nextTmpId = 1;
const newTmpPhaseId = () => `tmp_ph_${nextTmpId++}`;
const newTmpActivityId = () => `tmp_act_${nextTmpId++}`;
// Documents are matched positionally on the wire (no id field), so the
// local id is React-key-only and never leaves the client.
const newLocalDocId = () => `doc_${nextTmpId++}`;
// Deliverable ids are minted client-side + persisted verbatim (backend
// doesn't rewrite them), so use a stable random id rather than the
// tmp_-prefixed counter used for phases/activities. crypto.randomUUID is
// available in every browser this admin app targets.
const newDeliverableId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `del_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

function fromDetail(detail: FrameworkDetail): LocalState {
  return {
    domain: detail.domain,
    title: detail.title,
    short_description: detail.short_description,
    long_description: detail.long_description,
    tags: [...detail.tags],
    estimated_effort_label: detail.estimated_effort_label,
    maturity_target: {
      domain: detail.maturity_target?.domain ?? null,
      level: detail.maturity_target?.level ?? null,
    },
    deliverables: (detail.deliverables ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      description: d.description,
      evaluation_rubric: d.evaluation_rubric ?? '',
    })),
    phases: detail.phases.map((p) => ({
      id: p.id,
      title: p.title,
      description: p.description,
      tags: [...p.tags],
      order: p.order,
      activities: p.activities.map((a) => ({
        id: a.id,
        title: a.title,
        description: a.description,
        tags: [...a.tags],
        order: a.order,
        duration: {
          default_months: a.duration?.default_months ?? null,
          // Default to true (spec: "tenant can shorten/lengthen at
          // activation") when the field is missing on legacy records.
          tenant_override_allowed: a.duration?.tenant_override_allowed ?? true,
        },
        dependencies: (a.dependencies ?? []).map((d) => d.activity_id),
        stakeholders: [...(a.stakeholders ?? [])],
        documents: (a.documents ?? []).map((d) => ({
          localId: newLocalDocId(),
          source: d.source,
          role: d.role,
          title: d.title,
          kb_entry_id: d.kb_entry_id ?? '',
          uploaded_file_ref: d.uploaded_file_ref ?? '',
          produces_deliverable_ids: [...(d.produces_deliverable_ids ?? [])],
        })),
      })),
      documents: (p.documents ?? []).map((d) => ({
        localId: newLocalDocId(),
        source: d.source,
        title: d.title,
        kb_entry_id: d.kb_entry_id ?? '',
        uploaded_file_ref: d.uploaded_file_ref ?? '',
      })),
    })),
  };
}

function emptyState(): LocalState {
  return {
    domain: 'D1_strategy_org',
    title: '',
    short_description: '',
    long_description: '',
    tags: [],
    estimated_effort_label: null,
    maturity_target: { domain: null, level: null },
    deliverables: [],
    phases: [],
  };
}

/** Serialize local deliverables for the wire; drop empty rubrics to keep
 *  the persisted shape tidy (backend treats missing === undefined). */
function toDeliverableInputs(deliverables: LocalDeliverable[]): FrameworkDeliverable[] {
  return deliverables.map((d) => {
    const rubric = d.evaluation_rubric.trim();
    const base: FrameworkDeliverable = {
      id: d.id,
      name: d.name.trim(),
      description: d.description.trim(),
    };
    return rubric ? { ...base, evaluation_rubric: rubric } : base;
  });
}

/** Convert a LocalPhaseDocument to the wire shape — drops the client-only
 *  localId and the source-irrelevant field so the persisted document stays
 *  tidy. Rows missing their key ref (kb_entry_id / uploaded_file_ref) are
 *  dropped entirely; incomplete rows shouldn't clutter the DB. */
function toPhaseDocumentInputs(docs: LocalPhaseDocument[]): PhaseDocument[] {
  const out: PhaseDocument[] = [];
  for (const d of docs) {
    const title = d.title.trim();
    if (d.source === 'kb_entry_ref') {
      const ref = d.kb_entry_id.trim();
      if (!ref || !title) continue;
      out.push({ source: 'kb_entry_ref', role: 'reference', kb_entry_id: ref, title });
    } else {
      const ref = d.uploaded_file_ref.trim();
      if (!ref || !title) continue;
      out.push({ source: 'uploaded_file', role: 'reference', uploaded_file_ref: ref, title });
    }
  }
  return out;
}

/** Convert a LocalActivityDocument to the wire shape. Same drop-if-empty
 *  rule as phase documents: rows missing title or ref never persist.
 *  For `deliverable_template` role we require at least one produce claim
 *  — an empty-claim template row is a partially-filled scaffold, not
 *  something the backend should see (server-side rule 7 would reject on
 *  publish anyway; dropping here keeps drafts tidy). */
function toActivityDocumentInputs(docs: LocalActivityDocument[]): ActivityDocument[] {
  const out: ActivityDocument[] = [];
  for (const d of docs) {
    const title = d.title.trim();
    if (!title) continue;
    const ref =
      d.source === 'kb_entry_ref' ? d.kb_entry_id.trim() : d.uploaded_file_ref.trim();
    if (!ref) continue;
    if (d.role === 'deliverable_template' && d.produces_deliverable_ids.length === 0) {
      continue;
    }
    const base: ActivityDocument =
      d.source === 'kb_entry_ref'
        ? { source: 'kb_entry_ref', role: d.role, kb_entry_id: ref, title }
        : { source: 'uploaded_file', role: d.role, uploaded_file_ref: ref, title };
    if (d.role === 'deliverable_template') {
      out.push({ ...base, produces_deliverable_ids: [...d.produces_deliverable_ids] });
    } else {
      out.push(base);
    }
  }
  return out;
}

/** Convert a LocalActivity to the wire shape. `duration` is only sent
 *  when a bucket is selected (backend validator requires it for publish
 *  but drafts can be saved without one). */
function toActivityInput(a: LocalActivity): ActivityInput {
  const base: ActivityInput = {
    id: a.id,
    title: a.title,
    description: a.description,
    tags: a.tags,
    order: a.order,
  };
  if (a.duration.default_months) {
    base.duration = {
      default_months: a.duration.default_months,
      tenant_override_allowed: a.duration.tenant_override_allowed,
    };
  }
  if (a.dependencies.length) {
    base.dependencies = a.dependencies.map((activity_id) => ({ activity_id }));
  }
  if (a.stakeholders.length) {
    base.stakeholders = a.stakeholders;
  }
  base.documents = toActivityDocumentInputs(a.documents);
  return base;
}

function toPhaseInputs(phases: LocalPhase[]): PhaseInput[] {
  return phases.map((p) => ({
    id: p.id,
    title: p.title,
    description: p.description,
    tags: p.tags,
    order: p.order,
    activities: p.activities.map(toActivityInput),
    documents: toPhaseDocumentInputs(p.documents),
  }));
}

/** Both dropdowns set → maturity_target is complete + persistable. */
function maturityTargetIfComplete(s: LocalState): MaturityTarget | null {
  const { domain, level } = s.maturity_target;
  if (!domain || !level) return null;
  return { domain, level };
}

export function FrameworkEditorPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  const mode: EditorMode = !routeId || routeId === 'new' ? 'new' : 'editing';
  const [detail, setDetail] = useState<FrameworkDetail | null>(null);
  const [state, setState] = useState<LocalState>(emptyState);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [isLoading, setIsLoading] = useState<boolean>(mode === 'editing');

  // Load existing framework on mount when in editing mode.
  useEffect(() => {
    if (mode !== 'editing' || !routeId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await getFramework(routeId);
      if (cancelled) return;
      if (result.ok) {
        setDetail(result.data.framework);
        setState(fromDetail(result.data.framework));
        setLoadingError(null);
      } else {
        setLoadingError(result.code === 'NOT_FOUND' ? 'not_found' : 'load_error');
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, routeId]);

  const canSave = useMemo(
    () =>
      state.title.trim() !== '' &&
      state.short_description.trim() !== '' &&
      detail?.status !== 'retired',
    [state.title, state.short_description, detail?.status],
  );

  // Which deliverable ids are claimed by at least one activity-level
  // deliverable_template document. Feeds the persistent rail's amber
  // "no template yet" chip + the publish blocker for unclaimed
  // deliverables. Recomputed on every state change; O(phases · activities
  // · docs) which is trivial for authoring-time cardinality.
  const claimedDeliverableIds = useMemo(() => {
    const claimed = new Set<string>();
    for (const phase of state.phases) {
      for (const activity of phase.activities) {
        for (const doc of activity.documents) {
          if (doc.role !== 'deliverable_template') continue;
          for (const id of doc.produces_deliverable_ids) claimed.add(id);
        }
      }
    }
    return claimed;
  }, [state.phases]);

  // Flat list of every activity in the framework — feeds the cross-phase
  // dependency picker on ActivityCard. Each entry carries enough context
  // for the label ("P2.3 · Draft governance policy") without the picker
  // having to re-walk state.phases.
  const allActivities = useMemo(() => {
    const flat: {
      id: string;
      title: string;
      phaseIndex: number;
      activityIndex: number;
    }[] = [];
    state.phases.forEach((phase, phaseIndex) => {
      phase.activities.forEach((activity, activityIndex) => {
        flat.push({
          id: activity.id,
          title: activity.title,
          phaseIndex,
          activityIndex,
        });
      });
    });
    return flat;
  }, [state.phases]);

  const publishBlockers = useMemo(() => {
    const errs: string[] = [];
    // v2 publish-readiness surface. Mirrors backend validateFrameworkPublishable
    // rules 1-7. Server-side guard remains the safety net; this list surfaces
    // reasons upfront so the reviewer doesn't try to publish blind.
    if (!maturityTargetIfComplete(state)) {
      errs.push(t('frameworks.publishBlockedMaturityTargetRequired'));
    }
    if (state.deliverables.length === 0) {
      errs.push(t('frameworks.publishBlockedNoDeliverables'));
    } else {
      const anyUnnamed = state.deliverables.some((d) => d.name.trim() === '');
      if (anyUnnamed) errs.push(t('frameworks.publishBlockedDeliverableUnnamed'));
      // Rule 7 (client-side preview): every declared deliverable must
      // be claimed by ≥1 activity `deliverable_template` document.
      const unclaimed = state.deliverables.filter((d) => !claimedDeliverableIds.has(d.id));
      if (unclaimed.length > 0) {
        errs.push(t('frameworks.publishBlockedDeliverableUnclaimed'));
      }
    }
    if (state.phases.length === 0) errs.push(t('frameworks.publishBlockedAtLeastOnePhase'));
    let sawEmptyPhase = false;
    let sawMissingDuration = false;
    for (const phase of state.phases) {
      if (phase.activities.length === 0) {
        sawEmptyPhase = true;
      }
      for (const activity of phase.activities) {
        if (!activity.duration.default_months) {
          sawMissingDuration = true;
        }
      }
    }
    if (sawEmptyPhase) errs.push(t('frameworks.publishBlockedPhaseNeedsActivity'));
    if (sawMissingDuration) errs.push(t('frameworks.publishBlockedActivityDurationMissing'));
    return errs;
  }, [state, t, claimedDeliverableIds]);

  const doSave = useCallback(async (): Promise<FrameworkDetail | null> => {
    if (!canSave) return null;
    setSaveState('saving');
    setSaveError(null);
    if (mode === 'new') {
      const longDesc = state.long_description.trim();
      const body: CreateFrameworkRequest = {
        domain: state.domain,
        title: state.title.trim(),
        short_description: state.short_description.trim(),
        estimated_effort_label: state.estimated_effort_label,
        ...(longDesc ? { long_description: longDesc } : {}),
        ...(state.tags.length ? { tags: state.tags } : {}),
        ...(maturityTargetIfComplete(state)
          ? { maturity_target: maturityTargetIfComplete(state)! }
          : {}),
        ...(state.deliverables.length
          ? { deliverables: toDeliverableInputs(state.deliverables) }
          : {}),
        ...(state.phases.length ? { phases: toPhaseInputs(state.phases) } : {}),
      };
      const result = await createFramework(body);
      if (!result.ok) {
        setSaveState('error');
        setSaveError(result.message ?? 'create_failed');
        return null;
      }
      setDetail(result.data.framework);
      setState(fromDetail(result.data.framework));
      setSaveState('saved');
      // Replace the URL so reloads work and the back button is sane.
      navigate(`/admin/knowledge/frameworks/${result.data.framework.id}`, { replace: true });
      return result.data.framework;
    }
    // editing
    if (!detail) return null;
    const body: PatchFrameworkRequest = {
      title: state.title.trim(),
      short_description: state.short_description.trim(),
      long_description: state.long_description,
      tags: state.tags,
      estimated_effort_label: state.estimated_effort_label,
      ...(maturityTargetIfComplete(state)
        ? { maturity_target: maturityTargetIfComplete(state)! }
        : {}),
      // Deliverables are always sent on patch (even empty) so removals
      // via the editor persist. Matches how `phases` is sent as an
      // authoritative replacement on every save.
      deliverables: toDeliverableInputs(state.deliverables),
      phases: toPhaseInputs(state.phases),
    };
    const result = await patchFramework(detail.id, body);
    if (!result.ok) {
      setSaveState('error');
      setSaveError(result.message ?? 'save_failed');
      return null;
    }
    setDetail(result.data.framework);
    setState(fromDetail(result.data.framework));
    setSaveState('saved');
    return result.data.framework;
  }, [canSave, mode, detail, state, navigate]);

  const doLifecycle = useCallback(
    async (action: PendingAction) => {
      if (!detail || !action) return;
      // Save in-flight edits first; lifecycle ops act on persisted state.
      if (action === 'publish' || action === 'republish') {
        const saved = await doSave();
        if (!saved) return;
      }
      setSaveState('saving');
      setSaveError(null);
      const fn = action === 'publish'
        ? publishFramework
        : action === 'retire'
          ? retireFramework
          : republishFramework;
      const result = await fn(detail.id);
      if (!result.ok) {
        setSaveState('error');
        setSaveError(result.message ?? `${action}_failed`);
        return;
      }
      setDetail(result.data.framework);
      setState(fromDetail(result.data.framework));
      setSaveState('saved');
      setPendingAction(null);
    },
    [detail, doSave],
  );

  // ─── Phase / activity mutators ────────────────────────────────────────

  const addPhase = () => {
    setState((s) => ({
      ...s,
      phases: [
        ...s.phases,
        {
          id: newTmpPhaseId(),
          title: '',
          description: '',
          tags: [],
          order: s.phases.length,
          activities: [],
          documents: [],
        },
      ],
    }));
  };

  const removePhase = (phaseId: string) => {
    setState((s) => {
      const removedActivityIds = new Set(
        s.phases.find((p) => p.id === phaseId)?.activities.map((a) => a.id) ?? [],
      );
      return {
        ...s,
        phases: s.phases
          .filter((p) => p.id !== phaseId)
          .map((p, i) => ({
            ...p,
            order: i,
            // Prune cross-phase activity deps pointing at any activity
            // that lived in the removed phase.
            activities: p.activities.map((a) => ({
              ...a,
              dependencies: a.dependencies.filter((d) => !removedActivityIds.has(d)),
            })),
          })),
      };
    });
  };

  const updatePhase = (phaseId: string, mut: (p: LocalPhase) => LocalPhase) => {
    setState((s) => ({
      ...s,
      phases: s.phases.map((p) => (p.id === phaseId ? mut(p) : p)),
    }));
  };

  const addActivity = (phaseId: string) => {
    updatePhase(phaseId, (p) => ({
      ...p,
      activities: [
        ...p.activities,
        {
          id: newTmpActivityId(),
          title: '',
          description: '',
          tags: [],
          order: p.activities.length,
          duration: { default_months: null, tenant_override_allowed: true },
          dependencies: [],
          stakeholders: [],
          documents: [],
        },
      ],
    }));
  };

  const removeActivity = (phaseId: string, activityId: string) => {
    updatePhase(phaseId, (p) => ({
      ...p,
      activities: p.activities
        .filter((a) => a.id !== activityId)
        .map((a, i) => ({ ...a, order: i })),
    }));
  };

  const updateActivity = (
    phaseId: string,
    activityId: string,
    mut: (a: LocalActivity) => LocalActivity,
  ) => {
    updatePhase(phaseId, (p) => ({
      ...p,
      activities: p.activities.map((a) => (a.id === activityId ? mut(a) : a)),
    }));
  };

  // ─── Phase document mutators (v2 — MR 5) ──────────────────────────────

  const addPhaseDocument = (phaseId: string) => {
    updatePhase(phaseId, (p) => ({
      ...p,
      documents: [
        ...p.documents,
        {
          localId: newLocalDocId(),
          source: 'kb_entry_ref',
          title: '',
          kb_entry_id: '',
          uploaded_file_ref: '',
        },
      ],
    }));
  };

  const removePhaseDocument = (phaseId: string, localDocId: string) => {
    updatePhase(phaseId, (p) => ({
      ...p,
      documents: p.documents.filter((d) => d.localId !== localDocId),
    }));
  };

  const updatePhaseDocument = (
    phaseId: string,
    localDocId: string,
    mut: (d: LocalPhaseDocument) => LocalPhaseDocument,
  ) => {
    updatePhase(phaseId, (p) => ({
      ...p,
      documents: p.documents.map((d) => (d.localId === localDocId ? mut(d) : d)),
    }));
  };

  // ─── Activity document mutators (v2 — MR 6) ───────────────────────────

  const addActivityDocument = (phaseId: string, activityId: string) => {
    updateActivity(phaseId, activityId, (a) => ({
      ...a,
      documents: [
        ...a.documents,
        {
          localId: newLocalDocId(),
          source: 'kb_entry_ref',
          role: 'reference',
          title: '',
          kb_entry_id: '',
          uploaded_file_ref: '',
          produces_deliverable_ids: [],
        },
      ],
    }));
  };

  const removeActivityDocument = (
    phaseId: string,
    activityId: string,
    localDocId: string,
  ) => {
    updateActivity(phaseId, activityId, (a) => ({
      ...a,
      documents: a.documents.filter((d) => d.localId !== localDocId),
    }));
  };

  const updateActivityDocument = (
    phaseId: string,
    activityId: string,
    localDocId: string,
    mut: (d: LocalActivityDocument) => LocalActivityDocument,
  ) => {
    updateActivity(phaseId, activityId, (a) => ({
      ...a,
      documents: a.documents.map((d) => (d.localId === localDocId ? mut(d) : d)),
    }));
  };

  // ─── Deliverable mutators ─────────────────────────────────────────────

  const addDeliverable = () => {
    setState((s) => ({
      ...s,
      deliverables: [
        ...s.deliverables,
        {
          id: newDeliverableId(),
          name: '',
          description: '',
          evaluation_rubric: '',
        },
      ],
    }));
  };

  const removeDeliverable = (deliverableId: string) => {
    setState((s) => ({
      ...s,
      deliverables: s.deliverables.filter((d) => d.id !== deliverableId),
    }));
  };

  const updateDeliverable = (
    deliverableId: string,
    mut: (d: LocalDeliverable) => LocalDeliverable,
  ) => {
    setState((s) => ({
      ...s,
      deliverables: s.deliverables.map((d) => (d.id === deliverableId ? mut(d) : d)),
    }));
  };

  // ─── Render ───────────────────────────────────────────────────────────

  if (mode === 'editing' && isLoading) {
    return (
      <AdminShell activeKey="frameworks" breadcrumb={[t('frameworks.breadcrumbKnowledge'), t('frameworks.breadcrumbFrameworks')]}>
        <div className={styles.loadingNote}>{t('frameworks.loading')}</div>
      </AdminShell>
    );
  }
  if (loadingError === 'not_found') {
    return (
      <AdminShell activeKey="frameworks" breadcrumb={[t('frameworks.breadcrumbKnowledge'), t('frameworks.breadcrumbFrameworks')]}>
        <div className={styles.loadingNote}>{t('frameworks.detailNotFound')}</div>
      </AdminShell>
    );
  }
  if (loadingError === 'load_error') {
    return (
      <AdminShell activeKey="frameworks" breadcrumb={[t('frameworks.breadcrumbKnowledge'), t('frameworks.breadcrumbFrameworks')]}>
        <div className={styles.loadingNote}>{t('frameworks.detailGenericError')}</div>
      </AdminShell>
    );
  }

  const editorialStatus = detail?.status ?? 'draft';
  const readOnly = editorialStatus === 'retired';

  return (
    <AdminShell
      activeKey="frameworks"
      breadcrumb={[
        t('frameworks.breadcrumbKnowledge'),
        t('frameworks.breadcrumbFrameworks'),
        detail?.title || t('frameworks.editorBreadcrumbFrameworkPlaceholder'),
      ]}
    >
      <PageHeader
        title=""
        sub=""
        actions={null}
      />

      <div className={styles.shellPad}>
        {/* Header row */}
        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <input
              type="text"
              className={styles.titleInput}
              placeholder={t('frameworks.editorTitlePlaceholder')}
              value={state.title}
              onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
              disabled={readOnly}
            />
            <div className={styles.headerMeta}>
              <span className={statusClass(editorialStatus)}>{statusLabel(editorialStatus, t)}</span>
              {detail && <span>v{detail.version}</span>}
              {detail && <span>{t(`kb.domain.${detail.domain}`)}</span>}
            </div>
          </div>
          <div className={styles.headerActions}>
            <span className={`${styles.savedIndicator} ${saveStateClass(saveState)}`}>
              {saveStateLabel(saveState, t)}
            </span>
            {editorialStatus !== 'retired' && (
              <Button kind="ghost" onClick={() => void doSave()} disabled={!canSave || saveState === 'saving'}>
                {t('frameworks.editorSaveDraft')}
              </Button>
            )}
            {editorialStatus === 'draft' && (
              <Button
                kind="primary"
                onClick={() => setPendingAction('publish')}
                disabled={publishBlockers.length > 0 || saveState === 'saving'}
              >
                {t('frameworks.editorPublish')}
              </Button>
            )}
            {editorialStatus === 'published' && (
              <Button kind="ghost" onClick={() => setPendingAction('retire')}>
                {t('frameworks.editorRetire')}
              </Button>
            )}
            {editorialStatus === 'retired' && (
              <Button
                kind="primary"
                onClick={() => setPendingAction('republish')}
                disabled={publishBlockers.length > 0 || saveState === 'saving'}
              >
                {t('frameworks.editorRepublish')}
              </Button>
            )}
          </div>
        </div>

        {saveError && (
          <div className={styles.errorBanner}>
            {t('frameworks.detailGenericError')} ({saveError})
          </div>
        )}

        {pendingAction && (
          <LifecycleConfirm
            action={pendingAction}
            onConfirm={() => void doLifecycle(pendingAction)}
            onCancel={() => setPendingAction(null)}
          />
        )}

        {publishBlockers.length > 0 && editorialStatus === 'draft' && (
          <div className={styles.publishBlocked}>
            <p className={styles.publishBlockedTitle}>{t('frameworks.publishBlockedTitle')}</p>
            <ul className={styles.publishBlockedList}>
              {publishBlockers.map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.mainAndRail}>
          <div className={styles.mainCol}>
        {/* Section: Meta */}
        <section className={styles.section}>
          <header className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('frameworks.sectionMeta')}</h2>
          </header>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>{t('frameworks.fieldDomain')}</label>
              <select
                className={styles.select}
                value={state.domain}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setState((s) => ({ ...s, domain: e.target.value as Domain }))
                }
                disabled={readOnly || mode === 'editing'}
              >
                {ALL_DOMAINS.map((d) => (
                  <option key={d} value={d}>
                    {t(`kb.domain.${d}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>{t('frameworks.fieldEstimatedEffort')}</label>
              <input
                type="text"
                className={styles.input}
                placeholder={t('frameworks.fieldEstimatedEffortPlaceholder')}
                value={state.estimated_effort_label ?? ''}
                onChange={(e) =>
                  setState((s) => ({ ...s, estimated_effort_label: e.target.value || null }))
                }
                disabled={readOnly}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>{t('frameworks.fieldShortDescription')}</label>
            <input
              type="text"
              className={styles.input}
              value={state.short_description}
              onChange={(e) =>
                setState((s) => ({ ...s, short_description: e.target.value }))
              }
              disabled={readOnly}
            />
            <span className={styles.fieldHint}>{t('frameworks.fieldShortDescriptionHint')}</span>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>{t('frameworks.fieldLongDescription')}</label>
            <textarea
              className={styles.textarea}
              value={state.long_description}
              onChange={(e) => setState((s) => ({ ...s, long_description: e.target.value }))}
              disabled={readOnly}
              rows={4}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>{t('frameworks.fieldTags')}</label>
            <TagInput
              value={state.tags}
              onChange={(tags) => setState((s) => ({ ...s, tags }))}
              placeholder={t('frameworks.fieldTagsPlaceholder')}
              disabled={readOnly}
            />
          </div>
        </section>

        {/* Section: Maturity Target — the outcome contract. Load-bearing
          * for the consumer library's recommendation bands ("Recommended
          * Next" / "Already There" / "Ambitious") + required for publish.
          * Two dropdowns: domain (D1-D8) + level (5 rungs). */}
        <section className={styles.section}>
          <header className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('frameworks.sectionMaturityTarget')}</h2>
          </header>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                {t('frameworks.maturityTargetDomainLabel')}
              </label>
              <select
                className={styles.select}
                value={state.maturity_target.domain ?? ''}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const v = e.target.value;
                  setState((s) => ({
                    ...s,
                    maturity_target: {
                      ...s.maturity_target,
                      domain: v === '' ? null : (v as Domain),
                    },
                  }));
                }}
                disabled={readOnly}
              >
                <option value="">{t('frameworks.maturityTargetDomainPlaceholder')}</option>
                {ALL_DOMAINS.map((d) => (
                  <option key={d} value={d}>
                    {t(`domains.${d}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                {t('frameworks.maturityTargetLevelLabel')}
              </label>
              <select
                className={styles.select}
                value={state.maturity_target.level ?? ''}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const v = e.target.value;
                  setState((s) => ({
                    ...s,
                    maturity_target: {
                      ...s.maturity_target,
                      level: v === '' ? null : (v as MaturityLevel),
                    },
                  }));
                }}
                disabled={readOnly}
              >
                <option value="">{t('frameworks.maturityTargetLevelPlaceholder')}</option>
                {ALL_MATURITY_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {t(`frameworks.maturityLevel.${lvl}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <span className={styles.fieldHint}>
            {t('frameworks.maturityTargetHint')}
          </span>
        </section>

        {/* Section: Deliverables (v2) — the tangible outputs the tenant
          * produces by running this framework. Repeater above phases per
          * the designer's spec. Persistent rail on the right lets the
          * editor see the declared set continuously while authoring
          * activities; forward/reverse pickers (linking deliverables to
          * activity-level `deliverable_template` documents) land in MR 6
          * once the activity documents subform exists. */}
        <section className={styles.section}>
          <header className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('frameworks.sectionDeliverables')}</h2>
          </header>
          <p className={styles.sectionIntro}>{t('frameworks.deliverablesIntro')}</p>

          {state.deliverables.length === 0 ? (
            <div className={styles.deliverablesEmpty}>
              <p className={styles.deliverablesEmptyPrompt}>
                {t('frameworks.deliverablesEmptyPrompt')}
              </p>
              <span className={styles.fieldHint}>
                {t('frameworks.deliverablesEmptyExamples')}
              </span>
            </div>
          ) : (
            <div className={styles.deliverableList}>
              {state.deliverables.map((deliverable, i) => (
                <DeliverableCard
                  key={deliverable.id}
                  deliverable={deliverable}
                  index={i}
                  readOnly={readOnly}
                  onPatch={(mut) => updateDeliverable(deliverable.id, mut)}
                  onRemove={() => removeDeliverable(deliverable.id)}
                />
              ))}
            </div>
          )}

          {!readOnly && (
            <div className={styles.addRow}>
              <Button kind="ghost" onClick={addDeliverable}>
                {t('frameworks.addDeliverableButton')}
              </Button>
            </div>
          )}
        </section>

        {/* Section: Phases & Activities */}
        <section className={styles.section}>
          <header className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('frameworks.sectionPhases')}</h2>
          </header>
          <div className={styles.phaseList}>
            {state.phases.map((phase, phaseIndex) => (
              <PhaseCard
                key={phase.id}
                phase={phase}
                phaseIndex={phaseIndex}
                allActivities={allActivities}
                deliverables={state.deliverables}
                readOnly={readOnly}
                onPatch={(mut) => updatePhase(phase.id, mut)}
                onRemove={() => removePhase(phase.id)}
                onAddActivity={() => addActivity(phase.id)}
                onPatchActivity={(actId, mut) => updateActivity(phase.id, actId, mut)}
                onRemoveActivity={(actId) => removeActivity(phase.id, actId)}
                onAddDocument={() => addPhaseDocument(phase.id)}
                onPatchDocument={(localDocId, mut) =>
                  updatePhaseDocument(phase.id, localDocId, mut)
                }
                onRemoveDocument={(localDocId) => removePhaseDocument(phase.id, localDocId)}
                onAddActivityDocument={(actId) => addActivityDocument(phase.id, actId)}
                onPatchActivityDocument={(actId, localDocId, mut) =>
                  updateActivityDocument(phase.id, actId, localDocId, mut)
                }
                onRemoveActivityDocument={(actId, localDocId) =>
                  removeActivityDocument(phase.id, actId, localDocId)
                }
              />
            ))}
          </div>
          {!readOnly && (
            <div className={styles.addRow}>
              <Button kind="ghost" onClick={addPhase}>
                {t('frameworks.addPhaseButton')}
              </Button>
            </div>
          )}
        </section>
          </div>

          {/* Persistent Deliverables rail — always visible while the editor
            * scrolls through phases / activities. Amber "no template yet"
            * chip logic activates in MR 6 (once activity documents can
            * claim deliverables via `produces_deliverable_ids`); until
            * then every declared deliverable renders as unclaimed, which
            * reflects reality. */}
          <aside className={styles.railCol}>
            <div className={styles.railSticky}>
              <DeliverablesRail
                deliverables={state.deliverables}
                claimedIds={claimedDeliverableIds}
              />
            </div>
          </aside>
        </div>
      </div>
    </AdminShell>
  );
}

// ─── Phase card ──────────────────────────────────────────────────────────

type FlatActivityRef = {
  id: string;
  title: string;
  phaseIndex: number;
  activityIndex: number;
};

function PhaseCard({
  phase,
  phaseIndex,
  allActivities,
  deliverables,
  readOnly,
  onPatch,
  onRemove,
  onAddActivity,
  onPatchActivity,
  onRemoveActivity,
  onAddDocument,
  onPatchDocument,
  onRemoveDocument,
  onAddActivityDocument,
  onPatchActivityDocument,
  onRemoveActivityDocument,
}: {
  phase: LocalPhase;
  phaseIndex: number;
  allActivities: FlatActivityRef[];
  deliverables: LocalDeliverable[];
  readOnly: boolean;
  onPatch: (mut: (p: LocalPhase) => LocalPhase) => void;
  onRemove: () => void;
  onAddActivity: () => void;
  onPatchActivity: (actId: string, mut: (a: LocalActivity) => LocalActivity) => void;
  onRemoveActivity: (actId: string) => void;
  onAddDocument: () => void;
  onPatchDocument: (
    localDocId: string,
    mut: (d: LocalPhaseDocument) => LocalPhaseDocument,
  ) => void;
  onRemoveDocument: (localDocId: string) => void;
  onAddActivityDocument: (actId: string) => void;
  onPatchActivityDocument: (
    actId: string,
    localDocId: string,
    mut: (d: LocalActivityDocument) => LocalActivityDocument,
  ) => void;
  onRemoveActivityDocument: (actId: string, localDocId: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.phaseCard}>
      <div className={styles.sectionHeader}>
        <span className={styles.phaseLabel}>
          {t('frameworks.phaseSectionLabel')} {phaseIndex + 1}
        </span>
        {!readOnly && (
          <button type="button" className={styles.removeButton} onClick={onRemove}>
            {t('frameworks.removePhaseButton')}
          </button>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.phaseTitleLabel')}</label>
        <input
          type="text"
          className={styles.input}
          value={phase.title}
          onChange={(e) => onPatch((p) => ({ ...p, title: e.target.value }))}
          disabled={readOnly}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.phaseDescriptionLabel')}</label>
        <textarea
          className={`${styles.textarea} ${styles.textareaShort}`}
          value={phase.description}
          onChange={(e) => onPatch((p) => ({ ...p, description: e.target.value }))}
          disabled={readOnly}
          rows={3}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.phaseTagsLabel')}</label>
        <TagInput
          value={phase.tags}
          onChange={(tags) => onPatch((p) => ({ ...p, tags }))}
          placeholder=""
          disabled={readOnly}
        />
      </div>

      {/* Phase-level reference documents — checklists, guides, examples
        * scoped to the whole phase. Role is locked to `reference` here;
        * deliverable templates live on activities since deliverables are
        * produced by discrete work items. */}
      <div className={styles.phaseDocsBlock}>
        <div className={styles.phaseDocsHeader}>
          <span className={styles.fieldLabel}>{t('frameworks.phaseDocumentsLabel')}</span>
          {!readOnly && (
            <button
              type="button"
              className={styles.phaseDocsAdd}
              onClick={onAddDocument}
            >
              {t('frameworks.addPhaseDocumentButton')}
            </button>
          )}
        </div>
        {phase.documents.length === 0 ? (
          <p className={styles.phaseDocsEmpty}>
            {t('frameworks.phaseDocumentsEmpty')}
          </p>
        ) : (
          <div className={styles.phaseDocsList}>
            {phase.documents.map((doc) => (
              <PhaseDocumentRow
                key={doc.localId}
                document={doc}
                readOnly={readOnly}
                onPatch={(mut) => onPatchDocument(doc.localId, mut)}
                onRemove={() => onRemoveDocument(doc.localId)}
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.activityList}>
        {phase.activities.map((activity, activityIndex) => (
          <ActivityCard
            key={activity.id}
            activity={activity}
            activityIndex={activityIndex}
            phaseIndex={phaseIndex}
            allActivities={allActivities}
            deliverables={deliverables}
            readOnly={readOnly}
            onPatch={(mut) => onPatchActivity(activity.id, mut)}
            onRemove={() => onRemoveActivity(activity.id)}
            onAddDocument={() => onAddActivityDocument(activity.id)}
            onPatchDocument={(localDocId, mut) =>
              onPatchActivityDocument(activity.id, localDocId, mut)
            }
            onRemoveDocument={(localDocId) =>
              onRemoveActivityDocument(activity.id, localDocId)
            }
          />
        ))}
        {!readOnly && (
          <div className={styles.addRow}>
            <Button kind="ghost" onClick={onAddActivity}>
              {t('frameworks.addActivityButton')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Activity card ───────────────────────────────────────────────────────

function ActivityCard({
  activity,
  activityIndex,
  phaseIndex,
  allActivities,
  deliverables,
  readOnly,
  onPatch,
  onRemove,
  onAddDocument,
  onPatchDocument,
  onRemoveDocument,
}: {
  activity: LocalActivity;
  activityIndex: number;
  phaseIndex: number;
  allActivities: FlatActivityRef[];
  deliverables: LocalDeliverable[];
  readOnly: boolean;
  onPatch: (mut: (a: LocalActivity) => LocalActivity) => void;
  onRemove: () => void;
  onAddDocument: () => void;
  onPatchDocument: (
    localDocId: string,
    mut: (d: LocalActivityDocument) => LocalActivityDocument,
  ) => void;
  onRemoveDocument: (localDocId: string) => void;
}) {
  const { t } = useI18n();
  const otherActivities = allActivities.filter((a) => a.id !== activity.id);

  return (
    <div className={styles.activityCard}>
      <div className={styles.sectionHeader}>
        <span className={styles.activityLabel}>
          {t('frameworks.activitySectionLabel')} {phaseIndex + 1}.{activityIndex + 1}
        </span>
        {!readOnly && (
          <button type="button" className={styles.removeButton} onClick={onRemove}>
            {t('frameworks.removeActivityButton')}
          </button>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.activityTitleLabel')}</label>
        <input
          type="text"
          className={styles.input}
          value={activity.title}
          onChange={(e) => onPatch((a) => ({ ...a, title: e.target.value }))}
          disabled={readOnly}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.activityDescriptionLabel')}</label>
        <textarea
          className={`${styles.textarea} ${styles.textareaShort}`}
          value={activity.description}
          onChange={(e) => onPatch((a) => ({ ...a, description: e.target.value }))}
          disabled={readOnly}
          rows={2}
        />
      </div>

      {/* Duration (v2). Bucket labels come from the calibration in
        * Olivier's Legal Strategic Plan sanity-check — see DurationBucket
        * in shared/frameworks.ts. tenant_override_allowed defaults true
        * per spec. */}
      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {t('frameworks.activityDurationLabel')}
          </label>
          <select
            className={styles.select}
            value={activity.duration.default_months ?? ''}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              const v = e.target.value;
              onPatch((a) => ({
                ...a,
                duration: {
                  ...a.duration,
                  default_months: v === '' ? null : (v as DurationBucket),
                },
              }));
            }}
            disabled={readOnly}
          >
            <option value="">{t('frameworks.activityDurationPlaceholder')}</option>
            {ALL_DURATION_BUCKETS.map((b) => (
              <option key={b} value={b}>
                {t(`frameworks.durationBucket.${b}`)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {t('frameworks.activityDurationOverrideLabel')}
          </label>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={activity.duration.tenant_override_allowed}
              onChange={(e) =>
                onPatch((a) => ({
                  ...a,
                  duration: { ...a.duration, tenant_override_allowed: e.target.checked },
                }))
              }
              disabled={readOnly}
            />
            <span>{t('frameworks.activityDurationOverrideDescription')}</span>
          </label>
        </div>
      </div>

      {/* Dependencies (v2). Cross-phase allowed. Excludes self. Chip
        * shows "P{phase}.{act} · Title" so ordering is legible even when
        * activities across phases share a title. */}
      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          {t('frameworks.activityDependenciesLabel')}
        </label>
        {otherActivities.length === 0 ? (
          <span className={styles.dependsEmpty}>
            {t('frameworks.activityDependenciesEmpty')}
          </span>
        ) : (
          <div className={styles.dependsList}>
            {otherActivities.map((other) => {
              const active = activity.dependencies.includes(other.id);
              const label = `P${other.phaseIndex + 1}.${other.activityIndex + 1} · ${
                other.title || t('frameworks.activityUntitled')
              }`;
              return (
                <button
                  type="button"
                  key={other.id}
                  className={`${styles.dependsChip} ${active ? styles.active : ''}`}
                  onClick={() =>
                    onPatch((a) => ({
                      ...a,
                      dependencies: active
                        ? a.dependencies.filter((d) => d !== other.id)
                        : [...a.dependencies, other.id],
                    }))
                  }
                  disabled={readOnly}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        <span className={styles.fieldHint}>
          {t('frameworks.activityDependenciesHint')}
        </span>
      </div>

      {/* Stakeholders (v2). TagInput with datalist suggestions from
        * STAKEHOLDER_STARTER_LIST; freeform entries always allowed. */}
      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          {t('frameworks.activityStakeholdersLabel')}
        </label>
        <TagInput
          value={activity.stakeholders}
          onChange={(stakeholders) => onPatch((a) => ({ ...a, stakeholders }))}
          placeholder={t('frameworks.activityStakeholdersPlaceholder')}
          suggestions={STAKEHOLDER_STARTER_LIST}
          disabled={readOnly}
        />
      </div>

      {/* Activity Documents (v2). Same shape as PhaseDocument plus a role
        * toggle; `deliverable_template` opens the produces_deliverable_ids
        * picker (the forward-link from the designer's spec). */}
      <div className={styles.phaseDocsBlock}>
        <div className={styles.phaseDocsHeader}>
          <span className={styles.fieldLabel}>
            {t('frameworks.activityDocumentsLabel')}
          </span>
          {!readOnly && (
            <button
              type="button"
              className={styles.phaseDocsAdd}
              onClick={onAddDocument}
            >
              {t('frameworks.addActivityDocumentButton')}
            </button>
          )}
        </div>
        {activity.documents.length === 0 ? (
          <p className={styles.phaseDocsEmpty}>
            {t('frameworks.activityDocumentsEmpty')}
          </p>
        ) : (
          <div className={styles.activityDocsList}>
            {activity.documents.map((doc) => (
              <ActivityDocumentRow
                key={doc.localId}
                document={doc}
                deliverables={deliverables}
                readOnly={readOnly}
                onPatch={(mut) => onPatchDocument(doc.localId, mut)}
                onRemove={() => onRemoveDocument(doc.localId)}
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.activityTagsLabel')}</label>
        <TagInput
          value={activity.tags}
          onChange={(tags) => onPatch((a) => ({ ...a, tags }))}
          placeholder=""
          disabled={readOnly}
        />
      </div>

    </div>
  );
}

// ─── Deliverable card + rail (v2 — MR 4) ─────────────────────────────────

function DeliverableCard({
  deliverable,
  index,
  readOnly,
  onPatch,
  onRemove,
}: {
  deliverable: LocalDeliverable;
  index: number;
  readOnly: boolean;
  onPatch: (mut: (d: LocalDeliverable) => LocalDeliverable) => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.deliverableCard}>
      <div className={styles.sectionHeader}>
        <span className={styles.deliverableLabel}>
          {t('frameworks.deliverableSectionLabel')} {index + 1}
        </span>
        {!readOnly && (
          <button type="button" className={styles.removeButton} onClick={onRemove}>
            {t('frameworks.removeDeliverableButton')}
          </button>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('frameworks.deliverableNameLabel')}</label>
        <input
          type="text"
          className={styles.input}
          placeholder={t('frameworks.deliverableNamePlaceholder')}
          value={deliverable.name}
          onChange={(e) => onPatch((d) => ({ ...d, name: e.target.value }))}
          disabled={readOnly}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          {t('frameworks.deliverableDescriptionLabel')}
        </label>
        <textarea
          className={`${styles.textarea} ${styles.textareaShort}`}
          value={deliverable.description}
          onChange={(e) => onPatch((d) => ({ ...d, description: e.target.value }))}
          disabled={readOnly}
          rows={2}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>
          {t('frameworks.deliverableRubricLabel')}
        </label>
        <textarea
          className={`${styles.textarea} ${styles.textareaShort}`}
          placeholder={t('frameworks.deliverableRubricPlaceholder')}
          value={deliverable.evaluation_rubric}
          onChange={(e) => onPatch((d) => ({ ...d, evaluation_rubric: e.target.value }))}
          disabled={readOnly}
          rows={2}
        />
        <span className={styles.fieldHint}>{t('frameworks.deliverableRubricHint')}</span>
      </div>
    </div>
  );
}

/**
 * Persistent rail listing every declared deliverable and whether a
 * template claims it. Sits in the editor's right column so the editor
 * feels the gap continuously while authoring activities, rather than
 * discovering it at publish time.
 *
 * MR 6 wired the dynamic claim signal — an entry flips from amber
 * "no template yet" to a green "template claimed" chip the moment any
 * activity-level `deliverable_template` document lists this deliverable
 * id in `produces_deliverable_ids`.
 */
function DeliverablesRail({
  deliverables,
  claimedIds,
}: {
  deliverables: LocalDeliverable[];
  claimedIds: ReadonlySet<string>;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.rail}>
      <h3 className={styles.railTitle}>{t('frameworks.railDeliverablesTitle')}</h3>
      {deliverables.length === 0 ? (
        <p className={styles.railEmpty}>{t('frameworks.railEmpty')}</p>
      ) : (
        <ul className={styles.railList}>
          {deliverables.map((d) => {
            const displayName = d.name.trim() || t('frameworks.railUnnamed');
            const claimed = claimedIds.has(d.id);
            return (
              <li key={d.id} className={styles.railItem}>
                <span className={styles.railItemName}>{displayName}</span>
                {claimed ? (
                  <span className={styles.railChipGreen}>
                    {t('frameworks.railTemplateClaimed')}
                  </span>
                ) : (
                  <span className={styles.railChipAmber}>
                    {t('frameworks.railNoTemplateYet')}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Phase document row (v2 · MR 5) ──────────────────────────────────────

/**
 * One phase-level document row. Compact horizontal layout: source select,
 * title, source-conditional ref field, remove button. Rows without both
 * title + ref are silently dropped at save time (see toPhaseDocumentInputs)
 * so partially-filled scaffolding doesn't clutter the DB.
 *
 * V1 UX: paste the KB entry id or the uploaded-file storage ref. A
 * proper KB picker + drag-drop upload ships in V1.x.
 */
function PhaseDocumentRow({
  document,
  readOnly,
  onPatch,
  onRemove,
}: {
  document: LocalPhaseDocument;
  readOnly: boolean;
  onPatch: (mut: (d: LocalPhaseDocument) => LocalPhaseDocument) => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.phaseDocRow}>
      <select
        className={styles.select}
        value={document.source}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onPatch((d) => ({ ...d, source: e.target.value as DocumentSource }))
        }
        disabled={readOnly}
      >
        <option value="kb_entry_ref">{t('frameworks.docSourceKbEntry')}</option>
        <option value="uploaded_file">{t('frameworks.docSourceUpload')}</option>
      </select>
      <input
        type="text"
        className={styles.input}
        placeholder={t('frameworks.docTitlePlaceholder')}
        value={document.title}
        onChange={(e) => onPatch((d) => ({ ...d, title: e.target.value }))}
        disabled={readOnly}
      />
      {document.source === 'kb_entry_ref' ? (
        <input
          type="text"
          className={styles.input}
          placeholder={t('frameworks.docKbIdPlaceholder')}
          value={document.kb_entry_id}
          onChange={(e) => onPatch((d) => ({ ...d, kb_entry_id: e.target.value }))}
          disabled={readOnly}
        />
      ) : (
        <input
          type="text"
          className={styles.input}
          placeholder={t('frameworks.docFileRefPlaceholder')}
          value={document.uploaded_file_ref}
          onChange={(e) =>
            onPatch((d) => ({ ...d, uploaded_file_ref: e.target.value }))
          }
          disabled={readOnly}
        />
      )}
      {!readOnly && (
        <button
          type="button"
          className={styles.removeButton}
          onClick={onRemove}
          aria-label={t('frameworks.removePhaseDocumentButton')}
        >
          ×
        </button>
      )}
    </div>
  );
}

// ─── Activity document row (v2 · MR 6) ───────────────────────────────────

/**
 * One activity-level document row. Adds two things vs PhaseDocumentRow:
 *   1. Role toggle (reference vs deliverable_template)
 *   2. When role === 'deliverable_template', a produces_deliverable_ids
 *      multi-select — the "forward link" from the designer's spec.
 *      Multi-valued per Q4: one template can feed several deliverables
 *      (Olivier's Risk Map example).
 *
 * Rows without a title / ref / (for templates) at least one claim are
 * dropped at save time; see toActivityDocumentInputs.
 */
function ActivityDocumentRow({
  document,
  deliverables,
  readOnly,
  onPatch,
  onRemove,
}: {
  document: LocalActivityDocument;
  deliverables: LocalDeliverable[];
  readOnly: boolean;
  onPatch: (mut: (d: LocalActivityDocument) => LocalActivityDocument) => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className={styles.activityDocCard}>
      <div className={styles.activityDocTopRow}>
        <select
          className={styles.select}
          value={document.source}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            onPatch((d) => ({ ...d, source: e.target.value as DocumentSource }))
          }
          disabled={readOnly}
          aria-label={t('frameworks.docSourceLabel')}
        >
          <option value="kb_entry_ref">{t('frameworks.docSourceKbEntry')}</option>
          <option value="uploaded_file">{t('frameworks.docSourceUpload')}</option>
        </select>
        <select
          className={styles.select}
          value={document.role}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            const nextRole = e.target.value as DocumentRole;
            onPatch((d) => ({
              ...d,
              role: nextRole,
              // Switching away from template clears the claims — they
              // are meaningless for reference docs.
              produces_deliverable_ids:
                nextRole === 'deliverable_template' ? d.produces_deliverable_ids : [],
            }));
          }}
          disabled={readOnly}
          aria-label={t('frameworks.docRoleLabel')}
        >
          <option value="reference">{t('frameworks.docRoleReference')}</option>
          <option value="deliverable_template">
            {t('frameworks.docRoleDeliverableTemplate')}
          </option>
        </select>
        <input
          type="text"
          className={styles.input}
          placeholder={t('frameworks.docTitlePlaceholder')}
          value={document.title}
          onChange={(e) => onPatch((d) => ({ ...d, title: e.target.value }))}
          disabled={readOnly}
        />
        {!readOnly && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={onRemove}
            aria-label={t('frameworks.removeActivityDocumentButton')}
          >
            ×
          </button>
        )}
      </div>

      {document.source === 'kb_entry_ref' ? (
        <input
          type="text"
          className={styles.input}
          placeholder={t('frameworks.docKbIdPlaceholder')}
          value={document.kb_entry_id}
          onChange={(e) => onPatch((d) => ({ ...d, kb_entry_id: e.target.value }))}
          disabled={readOnly}
        />
      ) : (
        <input
          type="text"
          className={styles.input}
          placeholder={t('frameworks.docFileRefPlaceholder')}
          value={document.uploaded_file_ref}
          onChange={(e) =>
            onPatch((d) => ({ ...d, uploaded_file_ref: e.target.value }))
          }
          disabled={readOnly}
        />
      )}

      {/* Forward link — produces_deliverable_ids picker. Only shown for
        * templates. Chips list every framework-level deliverable; click
        * toggles claim. Nudge copy when no deliverables are declared
        * yet points the editor to the Deliverables section. */}
      {document.role === 'deliverable_template' && (
        <div className={styles.docProducesBlock}>
          <label className={styles.fieldLabel}>
            {t('frameworks.docProducesLabel')}
          </label>
          {deliverables.length === 0 ? (
            <p className={styles.docProducesEmpty}>
              {t('frameworks.docProducesNoDeliverables')}
            </p>
          ) : (
            <div className={styles.dependsList}>
              {deliverables.map((d) => {
                const active = document.produces_deliverable_ids.includes(d.id);
                const label = d.name.trim() || t('frameworks.railUnnamed');
                return (
                  <button
                    type="button"
                    key={d.id}
                    className={`${styles.dependsChip} ${active ? styles.active : ''}`}
                    onClick={() =>
                      onPatch((doc) => ({
                        ...doc,
                        produces_deliverable_ids: active
                          ? doc.produces_deliverable_ids.filter((id) => id !== d.id)
                          : [...doc.produces_deliverable_ids, d.id],
                      }))
                    }
                    disabled={readOnly}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          <span className={styles.fieldHint}>{t('frameworks.docProducesHint')}</span>
        </div>
      )}
    </div>
  );
}

// ─── Tag input ───────────────────────────────────────────────────────────

// Simple module-scoped counter for unique datalist ids so multiple
// TagInputs on the same page don't collide. useId would be cleaner but
// this file already avoids it — the counter matches how ids are minted
// elsewhere (see nextTmpId).
let nextDatalistId = 1;

function TagInput({
  value,
  onChange,
  placeholder,
  disabled,
  suggestions,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  disabled: boolean;
  /** Optional autocomplete suggestions. Freeform entries are still
   *  allowed — the datalist is a hint, not a restriction. */
  suggestions?: ReadonlyArray<string>;
}) {
  const [draft, setDraft] = useState('');
  const [datalistId] = useState(() =>
    suggestions ? `tag-suggestions-${nextDatalistId++}` : '',
  );

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setDraft('');
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className={styles.tagBar}>
      {value.map((tag) => (
        <span key={tag} className={styles.tag}>
          {tag}
          {!disabled && (
            <button
              type="button"
              className={styles.tagRemove}
              onClick={() => onChange(value.filter((t) => t !== tag))}
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <>
          <input
            type="text"
            className={styles.tagInput}
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={commit}
            {...(suggestions ? { list: datalistId } : {})}
          />
          {suggestions && (
            <datalist id={datalistId}>
              {suggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </>
      )}
    </div>
  );
}

// ─── Lifecycle confirm panel ─────────────────────────────────────────────

function LifecycleConfirm({
  action,
  onConfirm,
  onCancel,
}: {
  action: NonNullable<PendingAction>;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const titleKey =
    action === 'publish'
      ? 'frameworks.publishConfirmTitle'
      : action === 'retire'
        ? 'frameworks.retireConfirmTitle'
        : 'frameworks.republishConfirmTitle';
  const bodyKey =
    action === 'publish'
      ? 'frameworks.publishConfirmBody'
      : action === 'retire'
        ? 'frameworks.retireConfirmBody'
        : 'frameworks.republishConfirmBody';
  const yesKey =
    action === 'publish'
      ? 'frameworks.publishConfirmYes'
      : action === 'retire'
        ? 'frameworks.retireConfirmYes'
        : 'frameworks.republishConfirmYes';
  const cancelKey =
    action === 'publish'
      ? 'frameworks.publishConfirmCancel'
      : action === 'retire'
        ? 'frameworks.retireConfirmCancel'
        : 'frameworks.republishConfirmCancel';
  return (
    <div className={styles.confirmPanel}>
      <h3 className={styles.confirmTitle}>{t(titleKey)}</h3>
      <p className={styles.confirmBody}>{t(bodyKey)}</p>
      <div className={styles.confirmRow}>
        <Button
          kind={action === 'retire' ? 'ghost' : 'primary'}
          onClick={onConfirm}
        >
          {t(yesKey)}
        </Button>
        <Button kind="ghost" onClick={onCancel}>
          {t(cancelKey)}
        </Button>
      </div>
    </div>
  );
}

// ─── Status helpers ──────────────────────────────────────────────────────

function statusClass(s: 'draft' | 'published' | 'retired'): string {
  if (s === 'draft') return styles['statusDraft'] ?? '';
  if (s === 'published') return styles['statusPublished'] ?? '';
  return styles['statusRetired'] ?? '';
}
function statusLabel(s: 'draft' | 'published' | 'retired', t: (k: string) => string): string {
  if (s === 'draft') return t('frameworks.statusDraft');
  if (s === 'published') return t('frameworks.statusPublished');
  return t('frameworks.statusRetired');
}

function saveStateClass(s: SaveState): string {
  if (s === 'saving') return styles['saving'] ?? '';
  if (s === 'saved') return styles['saved'] ?? '';
  return '';
}
function saveStateLabel(s: SaveState, t: (k: string) => string): string {
  if (s === 'saving') return t('frameworks.editorSaving');
  if (s === 'saved') return t('frameworks.editorSaved');
  return '';
}
